import { InteractionStatus, RedirectRequest } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { CoherenceTheme } from "@coherence-design-system/styles";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import { ThemeProvider, loadTheme } from "@fluentui/react";
import { FluentProvider, makeStyles, webLightTheme } from "@fluentui/react-components";

import { scopes } from "./common/msal";
import CafeSync from "./components/CafeSync";
import DialogBox from "./components/DialogBox";
import HeaderComponent from "./components/Header";
import ImageManagement from "./components/ImageManagement";
import Loader from "./components/Loader/Loader";
import MenuManagement from "./components/MenuManagement";
import NavBar from "./components/NavBar";

initializeIcons();
loadTheme(CoherenceTheme);

const useClasses = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: "#fafafa",
    minHeight: 0,
    minWidth: 0,
  },
  body: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: "auto",
  },
  nav: {
    width: "48px",
    flexShrink: 0,
  },
  content: {
    minHeight: 0,
    minWidth: 0,
    width: "100%",
  },
});

const App = () => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const classes = useClasses();

  useEffect(() => {
    if (isAuthenticated || inProgress !== InteractionStatus.None) return;

    const request: RedirectRequest = {
      scopes: [scopes.diningApi],
    };

    instance.loginRedirect(request);
  }, [instance, inProgress, isAuthenticated]);

  if (!isAuthenticated) {
    return <Loader loaderText="Loading..." />;
  }

  return (
    <FluentProvider theme={webLightTheme} targetDocument={typeof window !== "undefined" ? window.document : undefined}>
      <ThemeProvider theme={CoherenceTheme} applyTo="body">
        <BrowserRouter data-testid="RouterLevel">
          <DialogBox />
          <div className={classes.root}>
            <HeaderComponent />

            <main id="skipToMainContent" tabIndex={-1} className={classes.body}>
              <div className={classes.nav}>
                <NavBar />
              </div>

              <div className={classes.content} id="pageContent">
                <Suspense fallback={<Loader loaderText="Loading page..." />}>
                  <Routes>
                    <Route path="/" element={<MenuManagement />} />
                    <Route path="/menus" element={<MenuManagement />} />
                    <Route path="/images" element={<ImageManagement />} />
                    <Route path="/cafeSync" element={<CafeSync />} />
                  </Routes>
                </Suspense>
              </div>
            </main>
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </FluentProvider>
  );
};

export default App;
